import { CheckCircle, PlayArrow, PlayCircle, Schedule, Visibility } from '@mui/icons-material';
import { Avatar, Box, Button, Card, CardContent, Chip, LinearProgress, Typography, useTheme } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { ELinks } from '@sharedInterfaces/globalEnums';
import { selectCurrentUserOnboardingTalk } from '@store/reducer/employeeTalksReducer';
import type { AppState } from '@store/store';
import React, { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { useOnboardingStrings } from '../Onboarding/data/OnboardingLangStrings';

type OnboardingStatus = 'not-started' | 'in-progress' | 'completed';

interface OnboardingStatusWidgetProps {
    employeeId?: number;
    status?: OnboardingStatus;
    progress?: number;
    completedDate?: Date;
    onStart?: () => void;
    onContinue?: () => void;
    onViewResults?: () => void;
}

export default function OnboardingStatusWidget({
    status,
    progress = 0,
    completedDate,
    onStart,
    onContinue,
    onViewResults,
}: OnboardingStatusWidgetProps) {
    const theme = useTheme();
    const navigate = useNavigate();
    const lang = useSelector((state: AppState) => state.employee.language);
    const onboardingTalk = useSelector(selectCurrentUserOnboardingTalk);
    const onboardingStrings = useOnboardingStrings(lang as 'de' | 'en' | 'th');

    const [actualStatus, setActualStatus] = useState<OnboardingStatus>(status || 'not-started');
    const [actualProgress, setActualProgress] = useState(progress);

    const checkOnboardingState = useCallback(() => {
        if (!onboardingTalk) return;

        // Bestimme Status basierend auf EmployeeTalk
        if (onboardingTalk.employeeConfirmed) {
            setActualStatus('completed');
            setActualProgress(100);
        } else if (onboardingTalk.talkState && onboardingTalk.talkState.navigation.currentStep > 0) {
            setActualStatus('in-progress');
            const currentStep = onboardingTalk.talkState.navigation.currentStep || 0;
            const totalSteps = 6; // Standard Onboarding hat 6 Schritte
            setActualProgress(Math.round(((currentStep + 1) / totalSteps) * 100));
        } else {
            setActualStatus('not-started');
            setActualProgress(0);
        }
    }, [onboardingTalk]);

    useEffect(() => {
        checkOnboardingState();
    }, [checkOnboardingState]);

    const getStatusConfig = () => {
        switch (actualStatus) {
            case 'not-started':
                return {
                    title: onboardingStrings.onboardingStart,
                    description: onboardingStrings.onboardingStartDescription,
                    color: theme.palette.primary.main,
                    bgColor: alpha(theme.palette.primary.main, 0.1),
                    icon: <PlayCircle />,
                    action: onboardingStrings.startNow,
                    actionIcon: <PlayArrow />,
                    onAction: onStart || handleStartOnboarding,
                };
            case 'in-progress':
                return {
                    title: onboardingStrings.onboardingContinue,
                    description: onboardingStrings.onboardingContinueDescription.replace(
                        '{progress}',
                        Math.round(actualProgress).toString(),
                    ),
                    color: theme.palette.warning.main,
                    bgColor: alpha(theme.palette.warning.main, 0.1),
                    icon: <Schedule />,
                    action: onboardingStrings.continueOnboarding,
                    actionIcon: <PlayArrow />,
                    onAction: onContinue || handleContinueOnboarding,
                };
            case 'completed':
                return {
                    title: onboardingStrings.onboardingCompleted,
                    description: completedDate
                        ? `Abgeschlossen am ${completedDate.toLocaleDateString(lang === 'de' ? 'de-DE' : lang === 'en' ? 'en-US' : 'th-TH')}`
                        : onboardingStrings.onboardingCompletedDescription,
                    color: theme.palette.success.main,
                    bgColor: alpha(theme.palette.success.main, 0.1),
                    icon: <CheckCircle />,
                    action: onboardingStrings.viewResults,
                    actionIcon: <Visibility />,
                    onAction: onViewResults || handleViewResults,
                };
        }
    };

    const handleStartOnboarding = () => {
        if (onboardingTalk) {
            navigate(`/${ELinks.EMPLOYEE_TALKS}/${onboardingTalk.id}`);
        }
    };

    const handleContinueOnboarding = () => {
        if (onboardingTalk) {
            navigate(`/${ELinks.EMPLOYEE_TALKS}/${onboardingTalk.id}`);
        }
    };

    const handleViewResults = () => {
        if (onboardingTalk) {
            navigate(`/${ELinks.EMPLOYEE_TALKS}/${onboardingTalk.id}`);
        }
    };

    // Nur anzeigen wenn Onboarding-Talk existiert
    if (!onboardingTalk) {
        return null;
    }

    const config = getStatusConfig();

    return (
        <Card
            elevation={1}
            sx={{
                border: `1px solid ${alpha(config.color, 0.2)}`,
                background: config.bgColor,
                transition: 'all 0.3s ease',
                '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: `0 8px 32px ${alpha(config.color, 0.2)}`,
                },
            }}
        >
            <CardContent sx={{ p: { xs: 2, sm: 3 }, textAlign: 'center' }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 2 }}>
                    <Avatar
                        sx={{
                            bgcolor: config.color,
                            width: 48,
                            height: 48,
                            mb: 2,
                        }}
                    >
                        {config.icon}
                    </Avatar>

                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, justifyContent: 'center' }}>
                        <Typography variant="h6" sx={{ fontWeight: 600 }}>
                            {config.title}
                        </Typography>
                        {actualStatus === 'completed' && (
                            <Chip
                                icon={<CheckCircle />}
                                label="Fertig"
                                size="small"
                                sx={{
                                    bgcolor: theme.palette.success.main,
                                    color: 'white',
                                    fontWeight: 600,
                                    '& .MuiChip-icon': {
                                        color: 'white',
                                    },
                                }}
                                aria-label="Onboarding abgeschlossen"
                            />
                        )}
                        {actualStatus === 'in-progress' && (
                            <Chip
                                label={`${Math.round(actualProgress)}%`}
                                size="small"
                                sx={{
                                    bgcolor:
                                        theme.palette.mode === 'dark'
                                            ? theme.palette.warning.main
                                            : theme.palette.warning.dark,
                                    color: 'white',
                                    fontWeight: 600,
                                    minWidth: '48px',
                                }}
                                aria-label={`Onboarding Fortschritt: ${Math.round(actualProgress)} Prozent`}
                            />
                        )}
                    </Box>

                    <Typography variant="body2" color="text.secondary">
                        {config.description}
                    </Typography>
                </Box>

                {/* Progress Bar for in-progress status */}
                {actualStatus === 'in-progress' && (
                    <Box sx={{ mb: 2 }}>
                        <LinearProgress
                            variant="determinate"
                            value={actualProgress}
                            sx={{
                                height: 6,
                                borderRadius: 3,
                                backgroundColor: alpha(config.color, 0.2),
                                '& .MuiLinearProgress-bar': {
                                    borderRadius: 3,
                                    backgroundColor: config.color,
                                },
                            }}
                        />
                    </Box>
                )}

                {/* Action Button */}
                <Button
                    variant="contained"
                    fullWidth
                    startIcon={config.actionIcon}
                    onClick={config.onAction}
                    sx={{
                        background:
                            actualStatus === 'completed'
                                ? `linear-gradient(45deg, ${theme.palette.success.main} 30%, ${theme.palette.primary.main} 90%)`
                                : actualStatus === 'in-progress'
                                  ? `linear-gradient(45deg, ${theme.palette.warning.main} 30%, ${theme.palette.secondary.main} 90%)`
                                  : `linear-gradient(45deg, ${theme.palette.primary.main} 30%, ${theme.palette.secondary.main} 90%)`,
                        color: 'white',
                        fontWeight: 600,
                        '&:hover': {
                            transform: 'translateY(-1px)',
                        },
                        transition: 'all 0.2s ease',
                    }}
                >
                    {config.action}
                </Button>

                {/* Additional Info */}
                {actualStatus === 'not-started' && (
                    <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ display: 'block', mt: 1, textAlign: 'center' }}
                    >
                        {onboardingStrings.estimatedDuration}
                    </Typography>
                )}

                {actualStatus === 'in-progress' && (
                    <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ display: 'block', mt: 1, textAlign: 'center' }}
                    >
                        {onboardingStrings.pauseAnytime}
                    </Typography>
                )}
            </CardContent>
        </Card>
    );
}

// Example usage component for testing
export function OnboardingStatusWidgetExample() {
    const [status, setStatus] = React.useState<OnboardingStatus>('not-started');
    const [progress, setProgress] = React.useState(0);

    const handleStart = () => {
        setStatus('in-progress');
        setProgress(20);
        console.log('Starting onboarding...');
    };

    const handleContinue = () => {
        console.log('Continuing onboarding...');
    };

    const handleViewResults = () => {
        console.log('Viewing onboarding results...');
    };

    return (
        <Box sx={{ maxWidth: 400, p: 2 }}>
            <OnboardingStatusWidget
                status={status}
                progress={progress}
                completedDate={status === 'completed' ? new Date() : undefined}
                onStart={handleStart}
                onContinue={handleContinue}
                onViewResults={handleViewResults}
            />

            {/* Demo Controls */}
            <Box sx={{ mt: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                <Button size="small" onClick={() => setStatus('not-started')}>
                    Not Started
                </Button>
                <Button
                    size="small"
                    onClick={() => {
                        setStatus('in-progress');
                        setProgress(60);
                    }}
                >
                    In Progress
                </Button>
                <Button size="small" onClick={() => setStatus('completed')}>
                    Completed
                </Button>
            </Box>
        </Box>
    );
}
